import React, { useState, useEffect, useCallback } from 'react';
import { pollsApi } from '../../api/polls';
import HrnbLoader from '../controls/HrnbLoader';
import '../../css/chats/PollMessage.css';

/**
 * Компонент для отображения опроса внутри сообщения чата.
 *
 * Получает данные опроса через pollsApi, отслеживает голос текущего пользователя,
 * позволяет голосовать, переголосовать, отменить голос и закрыть опрос.
 *
 * Обновления в реальном времени приходят через prop pollUpdate (WS-события),
 * которые обновляют данные опроса без перезагрузки.
 *
 * @param {Object} props
 * @param {string} props.chatId - ID чата (UUID)
 * @param {string} props.pollId - ID опроса (UUID)
 * @param {boolean} [props.isOwn] - Флаг собственного сообщения
 * @param {Object} [props.pollUpdate] - Обновлённые данные опроса из WS (PollResponse)
 */
export default function PollMessage({ chatId, pollId, isOwn = false, pollUpdate = null }) {
    const [poll, setPoll] = useState(null);
    const [myVote, setMyVote] = useState([]); // массив optionId
    const [loading, setLoading] = useState(true);
    const [voting, setVoting] = useState(false);
    const [closing, setClosing] = useState(false);
    const [error, setError] = useState(null);

    // Выбранные варианты до отправки голоса (для multiple choice)
    const [pendingSelection, setPendingSelection] = useState([]);

    // Загрузка опроса и своего голоса
    useEffect(() => {
        if (!chatId || !pollId) return;
        let cancelled = false;

        const loadData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [pollData, voteData] = await Promise.all([
                    pollsApi.getById(chatId, pollId),
                    pollsApi.getMyVote(chatId, pollId).catch(() => ({
                        pollId,
                        selectedOptionIds: [],
                    })),
                ]);
                if (cancelled) return;
                setPoll(pollData);
                const selectedIds = voteData?.selectedOptionIds || [];
                setMyVote(selectedIds);
                setPendingSelection(selectedIds);
            } catch (err) {
                if (!cancelled) {
                    console.error('[PollMessage] Failed to load poll:', err);
                    setError(err.response?.data?.message || 'Не удалось загрузить опрос');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadData();
        return () => { cancelled = true; };
    }, [chatId, pollId]);

    // Обработка WS-обновлений опроса (приходят через prop pollUpdate)
    useEffect(() => {
        if (pollUpdate && pollUpdate.id === pollId) {
            setPoll(pollUpdate);
        }
    }, [pollUpdate, pollId]);

    const totalVotes = poll?.options?.reduce((sum, opt) => sum + (opt.voteCount || 0), 0) || 0;
    const hasVoted = myVote.length > 0;
    const isClosed = poll?.isClosed === true;
    const isMultiple = poll?.multipleChoice === true;

    // Обработка клика по варианту
    const handleOptionClick = useCallback(async (optionId) => {
        if (isClosed || voting || !poll) return;

        // Для одиночного выбора — голосуем сразу
        if (!isMultiple) {
            setVoting(true);
            setError(null);
            try {
                await pollsApi.vote(chatId, pollId, [optionId]);
                const [updatedPoll, updatedVote] = await Promise.all([
                    pollsApi.getById(chatId, pollId),
                    pollsApi.getMyVote(chatId, pollId),
                ]);
                setPoll(updatedPoll);
                setMyVote(updatedVote?.selectedOptionIds || []);
                setPendingSelection(updatedVote?.selectedOptionIds || []);
            } catch (err) {
                console.error('[PollMessage] Vote failed:', err);
                setError(err.response?.data?.message || 'Не удалось проголосовать');
            } finally {
                setVoting(false);
            }
            return;
        }

        // Для множественного выбора — toggle в pendingSelection,
        // голосование по кнопке "Проголосовать"
        setPendingSelection(prev => {
            if (prev.includes(optionId)) {
                return prev.filter(id => id !== optionId);
            }
            return [...prev, optionId];
        });
    }, [chatId, pollId, isClosed, isMultiple, voting, poll]);

    // Отправка голоса для multiple choice
    const handleVoteMultiple = useCallback(async () => {
        if (isClosed || voting || pendingSelection.length === 0) return;
        setVoting(true);
        setError(null);
        try {
            await pollsApi.vote(chatId, pollId, pendingSelection);
            const [updatedPoll, updatedVote] = await Promise.all([
                pollsApi.getById(chatId, pollId),
                pollsApi.getMyVote(chatId, pollId),
            ]);
            setPoll(updatedPoll);
            setMyVote(updatedVote?.selectedOptionIds || []);
        } catch (err) {
            console.error('[PollMessage] Vote failed:', err);
            setError(err.response?.data?.message || 'Не удалось проголосовать');
        } finally {
            setVoting(false);
        }
    }, [chatId, pollId, isClosed, voting, pendingSelection]);

    // Отмена голоса
    const handleRemoveVote = useCallback(async () => {
        if (isClosed || voting || !hasVoted) return;
        setVoting(true);
        setError(null);
        try {
            await pollsApi.removeVote(chatId, pollId);
            const updatedPoll = await pollsApi.getById(chatId, pollId);
            setPoll(updatedPoll);
            setMyVote([]);
            setPendingSelection([]);
        } catch (err) {
            console.error('[PollMessage] Remove vote failed:', err);
            setError(err.response?.data?.message || 'Не удалось отменить голос');
        } finally {
            setVoting(false);
        }
    }, [chatId, pollId, isClosed, voting, hasVoted]);

    // Закрытие опроса
    const handleClosePoll = useCallback(async () => {
        if (isClosed || closing) return;
        setClosing(true);
        setError(null);
        try {
            const updatedPoll = await pollsApi.close(chatId, pollId);
            setPoll(updatedPoll);
        } catch (err) {
            console.error('[PollMessage] Close poll failed:', err);
            setError(err.response?.data?.message || 'Не удалось закрыть опрос');
        } finally {
            setClosing(false);
        }
    }, [chatId, pollId, isClosed, closing]);

    if (loading) {
        return (
            <div className="poll-message">
                <div className="poll-message__loading">
                    <HrnbLoader size="sm" />
                </div>
            </div>
        );
    }

    if (!poll) {
        return (
            <div className="poll-message">
                <div className="poll-message__error">Опрос не найден</div>
            </div>
        );
    }

    // Показываем прогресс-бары, если пользователь уже голосовал или опрос закрыт
    const showResults = hasVoted || isClosed;

    return (
        <div className="poll-message">
            <p className="poll-message__question">{poll.question}</p>

            {isMultiple && !isClosed && !hasVoted && (
                <div className="poll-message__multi-hint">
                    Можно выбрать несколько вариантов
                </div>
            )}

            <div className="poll-message__options">
                {(poll.options || [])
                    .slice()
                    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))
                    .map((option) => {
                        const isSelected = isMultiple
                            ? pendingSelection.includes(option.id)
                            : myVote.includes(option.id);
                        const percentage = option.percentage || 0;

                        return (
                            <div
                                key={option.id}
                                className={[
                                    'poll-message__option',
                                    isSelected ? 'poll-message__option--selected' : '',
                                    (isClosed || (hasVoted && !isMultiple)) ? 'poll-message__option--disabled' : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => handleOptionClick(option.id)}
                            >
                                {showResults && (
                                    <div
                                        className="poll-message__option-bar"
                                        style={{ width: `${percentage}%` }}
                                    />
                                )}
                                <div className="poll-message__option-content">
                                    <div className="poll-message__option-checkbox">
                                        {isSelected && (
                                            <span className="poll-message__option-checkbox-icon">✓</span>
                                        )}
                                    </div>
                                    <span className="poll-message__option-text">{option.text}</span>
                                    {showResults && (
                                        <span className="poll-message__option-percentage">
                                            {percentage.toFixed(1)}%
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
            </div>

            {/* Кнопка голосования для multiple choice */}
            {isMultiple && !isClosed && !hasVoted && pendingSelection.length > 0 && (
                <button
                    className="poll-message__close-btn"
                    onClick={handleVoteMultiple}
                    disabled={voting}
                    style={{ borderColor: 'rgba(35, 153, 227, 0.4)', color: '#4fb3ff' }}
                >
                    {voting ? <HrnbLoader size="sm" /> : `Проголосовать (${pendingSelection.length})`}
                </button>
            )}

            {/* Информация опроса */}
            <div className="poll-message__info">
                <span className="poll-message__votes-count">
                    📊 {totalVotes} {totalVotes === 1 ? 'голос' : totalVotes < 5 && totalVotes > 0 ? 'голоса' : 'голосов'}
                </span>
                <span className={`poll-message__status ${isClosed ? 'poll-message__status--closed' : ''}`}>
                    {isClosed ? '🔒 Опрос закрыт' : 'Опрос активен'}
                </span>
            </div>

            {/* Кнопка отмены голоса */}
            {hasVoted && !isClosed && (
                <button
                    className="poll-message__close-btn"
                    onClick={handleRemoveVote}
                    disabled={voting}
                >
                    {voting ? <HrnbLoader size="sm" /> : '↺ Отменить голос'}
                </button>
            )}

            {/* Кнопка закрытия опроса */}
            {!isClosed && (
                <button
                    className="poll-message__close-btn"
                    onClick={handleClosePoll}
                    disabled={closing}
                >
                    {closing ? <HrnbLoader size="sm" /> : '🔒 Закрыть опрос'}
                </button>
            )}

            {error && (
                <div className="poll-message__error">{error}</div>
            )}
        </div>
    );
}